import React, { useRef, useEffect } from 'react';
import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import { titleEditorStoreSelector, useTitleEditorStore } from '@warehouse/title/domain';
import { ChildrenColumnType } from '../children-compilation-extended.model';
import { UseEditEntryNumberOutput } from '../inline-edit/entry-number/use-edit-entry-number';
import { CellWrapper } from '../../../../src/components/library/SimpleTable/style';
import { TableTextInput } from '../../../../src/views/title/tab/productMetadata/style';
import { EntryNumberEditCellProps } from '../inline-edit/entry-number/EntryNumberEditCell';

interface EntryNumberDataGridCellProps {
	params: GridRenderCellParams<ChildrenColumnType, ChildrenColumnType>;
	editEntryNumber: UseEditEntryNumberOutput | null;
}

export function EntryNumberDataGridCell({ params, editEntryNumber }: EntryNumberDataGridCellProps) {
	const editMode = useTitleEditorStore(titleEditorStoreSelector.editMode);
	const inputRef = useRef<HTMLInputElement>(null);
	const cursorPositionRef = useRef<number | null>(null);

	// Get the entry number value from compilationEntries (source of truth) or fallback to row data
	const defaultEntryNumber =
		editEntryNumber?.compilationEntries?.find((entry) => entry.contentId === params.row.uuid)?.compilationEntryNumber ||
		('entryNumber' in params.row ? params.row.entryNumber : '');

	const props = simpleTableInfoToEntryNumberEditProps(editEntryNumber, params);

	// Restore cursor position after value updates (only runs when we have props)
	useEffect(() => {
		if (props && inputRef.current && cursorPositionRef.current !== null) {
			inputRef.current.setSelectionRange(cursorPositionRef.current, cursorPositionRef.current);
		}
	}, [props?.entryNumber, props]);

	// In edit mode and we have edit props, show the editable input
	if (editMode && props) {
		const { updateEntryNumber, commit, entryNumber } = props;

		const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
			// MUI DataGrid intercepts keyboard events, so we need to stop propagation
			// to allow cursor navigation within the input field
			if (
				e.key === 'ArrowLeft' ||
				e.key === 'ArrowRight' ||
				e.key === 'ArrowUp' ||
				e.key === 'ArrowDown' ||
				e.key === 'Home' ||
				e.key === 'End' ||
				e.key === 'Backspace' ||
				e.key === 'Delete'
			) {
				e.stopPropagation();
			}
		};
		return (
			<div style={{ paddingLeft: '8px' }}>
				<CellWrapper onClick={(e) => e.stopPropagation()}>
					<TableTextInput
						ref={inputRef}
						aria-label="Entry Number Input"
						enableFocusBorder={false}
						onChange={(e) => {
							// Store cursor position before update
							cursorPositionRef.current = e.target.selectionStart;
							updateEntryNumber(e.target.value);
						}}
						onBlur={() => commit()}
						onKeyDown={handleKeyDown}
						value={entryNumber}
					/>
				</CellWrapper>
			</div>
		);
	}

	// Otherwise, show read-only display
	return (
		<div
			style={{
				paddingLeft: '20px',
			}}
		>
			{defaultEntryNumber || ''}
		</div>
	);
}

function simpleTableInfoToEntryNumberEditProps(
	editEntryNumber: UseEditEntryNumberOutput | null,
	params: GridRenderCellParams<ChildrenColumnType, ChildrenColumnType>,
): EntryNumberEditCellProps | null {
	if (editEntryNumber) {
		const { commit, updateEntryNumber, compilationEntries } = editEntryNumber;

		const entryNumber = compilationEntries?.find(
			(entry) => entry.contentId === params.row.uuid,
		)?.compilationEntryNumber;

		return {
			commit,
			updateEntryNumber: (newEntryNumber: string | undefined) => updateEntryNumber(newEntryNumber, params.row.uuid),
			entryNumber,
		};
	}
	return null;
}
